<template>
    <div>
        <button
            type="button"
            class="button corrections-transition"
            :style="transitionStyle"
            :class="transitionClass"
            @click="show = true"
        >
            {{ transition.action_text }}
        </button>

        <portal to="modals">
            <ConfirmModal
                v-if="show"
                class="corrections-transition-modal"
                :class="{ 'corrections-transition-modal--reviewer': isReviewerModal }"
                :title="
                    isReviewerAddComment
                        ? $t('corrections.item.addComment')
                        : transition.action_text
                "
                :ok-disabled="!dataValid"
                buttons-reverse
                @canceled="show = false"
                @confirmed="changeState"
            >
                <template #content>
                    <Spinner v-if="loading" />
                    <form v-if="transition.schema" class="corrections-transition-modal__form">
                        <FormField
                            v-for="(field, name) in transition.schema"
                            :key="name"
                            :name="name"
                            :label="field.label"
                            :required="field.required"
                            :erorr="error"
                        >
                            <div>
                                <Wysiwyg
                                    v-model="data[name]"
                                    :placeholder="
                                        isReviewerAddComment
                                            ? $t('corrections.item.writeYourComment')
                                            : ''
                                    "
                                    :class="[
                                        'corrections-transition__comment',
                                        {
                                            'corrections-transition__comment--reviewer':
                                                isReviewerAddComment,
                                        },
                                        {
                                            'is-limit-exceeded':
                                                data[name].length > field.max_length
                                        }
                                    ]"
                                />
                            </div>
                            <div
                                v-if="data[name].length > field.max_length"
                                class="corrections-transition__comment-warnmsg"
                            >
                                Exceeded the limit
                            </div>
                        </FormField>
                    </form>
                </template>
            </ConfirmModal>
        </portal>
    </div>
</template>

<script>
import { ConfirmModal } from '@speechki-frontend/shared/ui/modals';
import { FormField } from '@speechki-frontend/shared/ui/forms';
import Wysiwyg from '@speechki-frontend/shared/ui/Wysiwyg';

import { changeCorrectionState } from '../../api/corrections';
import { mapGetters } from 'vuex';

const styleMapping = {
    text: 'color',
    background: 'background-color'
};

const convertHtmlToText = html => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

export default {
    name: 'CorrectionsTransition',
    components: {
        ConfirmModal,
        FormField,
        Wysiwyg
    },
    inject: {
        isEmbed: {
            default: false
        }
    },
    props: {
        transition: {
            type: Object,
            required: true
        },
        correctionId: {
            type: Number,
            required: true
        }
    },
    data() {
        return { show: false, data: {}, loading: false, error: undefined };
    },
    computed: {
        ...mapGetters(['isReviewer']),
        isReviewerModal() {
            return this.isEmbed && this.isReviewer;
        },
        isReviewerAddComment() {
            // "Add comment" is the backend `supplement_correction` transition
            // (`target` is the FSM transition name, not the localized label).
            return this.isReviewerModal && this.transition.target === 'supplement_correction';
        },
        dataValid() {
            const { schema } = this.transition;

            if (!schema) return true;

            return Object.entries(schema).every(([key, descriptor]) => {
                if (descriptor.required) {
                    if (!this.data[key]) return false;
                    const text = convertHtmlToText(this.data[key]);

                    return text.trim().length && this.data[key].length <= descriptor.max_length;
                }

                return true;
            });
        },
        transitionClass() {
            if (!this.transition.style.style) return '';

            return `button--${this.transition.style.style}`;
        },
        transitionStyle() {
            if (this.transition.style.style) return {};

            const { style } = this.transition;

            if (!style) return {};

            if (this.isReviewerModal) {
                // Professional Prooflistening redesign: button colors come from
                // the design (CorrectionsListItem.scss .correction--reviewer-card),
                // not from the backend transition style. Applying the backend's
                // inline `background` (e.g. #ff6700 for "Add comment") would
                // override the design's green primary button.
                return {};
            }

            // Outside the reviewer modal, preserve master's exact (buggy)
            // result instead of applying the fix above. Master called:
            //   Object.entries(style).reduce(([key, value]) => ({ [styleMapping[key]]: value }))
            // with NO initial value, which is a bug (see git history), but
            // it's the observable behavior this branch must keep matching.
            //
            // `Array.prototype.reduce` without an initial value on a
            // 2-element array calls the callback exactly once, with
            // (accumulator = entries[0], currentValue = entries[1]).
            // The callback destructures only its first argument, so it
            // reads `entries[0]` and completely ignores `entries[1]`
            // (the second style key, e.g. `background`, is silently
            // dropped — not `text`). For the real style shapes used by
            // correction transitions (`{ text: '#fff', background: '#ff6700' }`,
            // 2 keys, since the `style.style` shorthand branch above
            // already short-circuits the `{ style: 'outlined'|'primary' }`
            // shapes), master's effective output was just
            // `{ color: transition.style.text }` — background never applied.
            const entries = Object.entries(style);

            if (entries.length !== 2) return {};

            const [key, value] = entries[0];

            return { [styleMapping[key]]: value };
        }
    },
    created() {
        if (this.transition.schema) {
            Object.entries(this.transition.schema).forEach(([key]) => {
                this.data[key] = '';
            });
        }
    },
    methods: {
        async changeState() {
            try {
                this.loading = true;
                const { target } = this.transition;

                const { id } = await changeCorrectionState(this.correctionId, {
                    transition: target,
                    additional_data: this.data
                });
                this.$emit('update', id);
                this.show = false;
            } catch (e) {
                this.error = e;
                this.$store.dispatch('pushToast', {
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('corrections.item.statusError'),
                    message: this.$t('corrections.item.tryAgain')
                });
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<style src="./CorrectionsTransition.scss" lang="scss"></style>
